import { Controller } from '@hotwired/stimulus'
import { getRootFontSize } from '../utils/global'
import { createErrorHintElement } from '../utils/dom'

const DROPDOWN_THEME_PROPERTIES = [
  '--input-text-color-default',
  '--input-scrollbar-thumb-color',
  '--select-option-bg-hover',
  '--select-option-subtext-color'
]

/**
 * Контроллер кастомного select-компонента.
 *
 * Строит выпадающий список на основе нативного `<select>`, синхронизирует
 * выбранное значение, поддерживает поиск, навигацию с клавиатуры, отображение
 * изображений у опций и вывод ошибок в стиле формы.
 *
 * Выпадающий список рендерится в портал (`document.body` или модальное окно),
 * поэтому контроллер отдельно рассчитывает позицию и переносит CSS-переменные
 * темы.
 *
 * @example Базовое подключение
 * <div data-controller="select">
 *   <select>...</select>
 * </div>
 */
export default class SelectController extends Controller {
  static targets = [
    'toggle',
    'text',
    'placeholder',
    'searchWrapper',
    'errors',
    'error'
  ]

  static values = {
    portal: String,
    nothingFound: String,
    requiredMessage: String
  }

  /**
   * Все активные экземпляры контроллера.
   *
   * Используется, чтобы при открытии одного select закрывать остальные.
   *
   * @type {Set<SelectController>}
   */
  static instances = new Set()

  /**
   * Счётчик для генерации стабильных id опций, если у `<select>` нет `id`.
   *
   * @type {number}
   */
  static nextId = 0

  /**
   * Текущий DOM-элемент подсказки с ошибкой.
   *
   * @type {HTMLElement | null}
   */
  #errorHintElement = null

  /**
   * Закрывает все открытые select-компоненты, кроме переданного элемента.
   *
   * @param {HTMLElement} currentElement — корневой элемент select, который остаётся открытым
   */
  static closeOthers(currentElement) {
    this.instances.forEach((instance) => {
      if (instance.element !== currentElement && instance.isOpen) {
        instance.close()
      }
    })
  }

  /**
   * Инициализирует состояние контроллера, создаёт выпадающий список и
   * подписывает обработчики событий.
   */
  connect() {
    this.select = this.element.querySelector('select')
    this.isOpen = false
    this.openDirection = null
    this.repositionFrame = null
    this.activeOption = null

    this.optionIdPrefix = this.select.hasAttribute('id')
      ? this.select.getAttribute('id')
      : `select-option-${this.constructor.nextId++}`

    this.element.setAttribute('data-select-id', this.optionIdPrefix)

    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundRepositionDropdown = this.repositionDropdown.bind(this)

    if (!this.select) return

    if (this.select.required) this.validate = this.validateRequired.bind(this)

    this.buildDropdown()
    this.updateDisplay()
    this.toggleTarget.addEventListener('keydown', this.boundHandleKeydown)
    // Код снаружи меняет опции нативного <select> (например, отключает
    // прошедшее время в форме матча) и просит перерисовать список.
    this.boundRefresh = this.refresh.bind(this)
    this.element.addEventListener('select:refresh', this.boundRefresh)
    this.boundHandleChange = this.handleChange.bind(this)
    this.select.addEventListener('change', this.boundHandleChange)

    this.constructor.instances.add(this)
  }

  /**
   * Очищает созданный выпадающий список, отменяет ожидающее позиционирование
   * и снимает все подписки, добавленные контроллером.
   */
  disconnect() {
    if (this.repositionFrame) cancelAnimationFrame(this.repositionFrame)
    this.dropdown?.remove()
    this.toggleTarget?.removeEventListener('keydown', this.boundHandleKeydown)
    if (this.searchInput && this.boundHandleSearchInput) {
      this.searchInput.removeEventListener('input', this.boundHandleSearchInput)
    }
    document.removeEventListener('click', this.boundHandleClickOutside)
    document.removeEventListener('scroll', this.boundRepositionDropdown, true)
    window.removeEventListener('scroll', this.boundRepositionDropdown, true)
    window.removeEventListener('resize', this.boundRepositionDropdown)
    if (this.select && this.boundHandleChange) {
      this.select.removeEventListener('change', this.boundHandleChange)
    }
    if (this.boundRefresh) {
      this.element.removeEventListener('select:refresh', this.boundRefresh)
    }
    this.constructor.instances.delete(this)
  }

  /**
   * Обработчик изменения нативного `<select>`.
   *
   * При пользовательском изменении дополнительно сбрасывает состояние ошибки.
   *
   * @param {Event} e — событие `change`
   */
  handleChange(e) {
    this.updateDisplay()
    if (e.isTrusted) {
      this.reset()
    }
  }

  /**
   * Создаёт DOM-структуру выпадающего списка, копирует модификаторы,
   * добавляет поиск и переносит список в портал.
   */
  buildDropdown() {
    this.dropdown = Object.assign(document.createElement('div'), {
      className: 'select__dropdown',
      id: `${this.optionIdPrefix}-listbox`,
      role: 'listbox'
    })
    this.toggleTarget.setAttribute('aria-controls', this.dropdown.id)

    // Копируем модификаторы
    ;[...this.element.classList]
      .filter((cls) => cls.startsWith('select--'))
      .forEach((cls) =>
        this.dropdown.classList.add(
          cls.replace('select--', 'select__dropdown--')
        )
      )

    // Добавляем поиск
    if (this.hasSearchWrapperTarget) {
      const searchClone = this.searchWrapperTarget.cloneNode(true)
      searchClone.style.display = ''
      this.searchInput = searchClone.querySelector("input[type='text']")
      if (this.searchInput) {
        this.boundHandleSearchInput = this.handleSearchInput.bind(this)
        this.searchInput.addEventListener('input', this.boundHandleSearchInput)
        this.searchInput.addEventListener('keydown', this.boundHandleKeydown)
      }
      this.dropdown.appendChild(searchClone)
    }

    // Создаем контейнер опций
    this.optionsContainer = Object.assign(document.createElement('ul'), {
      className: 'select__options'
    })
    const wrapper = Object.assign(document.createElement('div'), {
      className: 'select__options-wrapper'
    })
    wrapper.appendChild(this.optionsContainer)
    this.dropdown.appendChild(wrapper)

    this.renderOptions()
    this.syncDropdownTheme()

    this.portalTarget.appendChild(this.dropdown)
  }

  /**
   * Синхронизирует CSS-переменные темы с корневого select на выпадающий список.
   */
  syncDropdownTheme() {
    const sourceStyles = getComputedStyle(this.element)

    DROPDOWN_THEME_PROPERTIES.forEach((propertyName) => {
      const propertyValue = sourceStyles.getPropertyValue(propertyName)
      if (propertyValue) {
        this.dropdown.style.setProperty(propertyName, propertyValue.trim())
      }
    })
  }

  /**
   * Возвращает контейнер, в который должен быть перенесён выпадающий список.
   *
   * Для select внутри модального окна используется wrapper модалки, чтобы
   * позиционирование оставалось в её координатах.
   *
   * @returns {HTMLElement}
   */
  get portalTarget() {
    if (this.portalValue === 'modal') {
      return this.element.closest('.modal__wrapper') || document.body
    }

    return document.body
  }

  /**
   * Рендерит список опций выпадающего списка на основе опций нативного `<select>`.
   *
   * Опции-заполнители пропускаются, а data-атрибуты используются для значения
   * и изображения рядом с текстом.
   */
  renderOptions() {
    this.optionsContainer.innerHTML = ''
    const isRadio = this.element.classList.contains('select--radio')

    Array.from(this.select.options).forEach((option, index) => {
      if (option.hasAttribute('data-placeholder')) return

      const li = document.createElement('li')
      li.id = `${this.optionIdPrefix}-${index}`
      li.className = 'select__option'
      li.setAttribute('role', 'option')
      li.dataset.value = option.value
      li.setAttribute('aria-selected', option.selected)
      // Отключённая опция (<option disabled>) не выбирается: .is-disabled
      // скрывает её (см. _select.scss), а visibleOptions пропускает при
      // навигации с клавиатуры.
      if (option.disabled) {
        li.classList.add('is-disabled')
        li.setAttribute('aria-disabled', 'true')
      }

      const markSpan = document.createElement('span')
      markSpan.className = 'select__option-mark'

      const textSpan = document.createElement('span')
      textSpan.className = 'select__option-text'
      const labelSpan = document.createElement('span')
      labelSpan.textContent = option.textContent
      textSpan.appendChild(labelSpan)

      const image = option.dataset.image
      let imageTag = null
      if (image) {
        imageTag = document.createElement('img')
        imageTag.src = image
        imageTag.alt = ''
        imageTag.width = 18
        imageTag.height = 18
      }

      // Для radio - радиокнопка перед текстом, для обычного - галочка после
      if (isRadio) {
        li.append(markSpan, imageTag || '', textSpan)
      } else {
        li.append(imageTag || '', textSpan, markSpan)
      }

      li.onclick = () => {
        if (!option.disabled) this.selectOption(option.value)
      }
      this.optionsContainer.appendChild(li)
    })
  }

  /**
   * Перерисовывает выпадающий список и подпись по текущим опциям `<select>`.
   */
  refresh() {
    this.renderOptions()
    this.updateDisplay()
  }

  /**
   * Возвращает опции, доступные после фильтрации поиском.
   *
   * @returns {HTMLElement[]}
   */
  get visibleOptions() {
    return Array.from(
      this.optionsContainer.querySelectorAll('.select__option')
    ).filter(
      (option) =>
        !option.classList.contains('is-hidden') &&
        !option.classList.contains('is-disabled')
    )
  }

  /**
   * Возвращает DOM-элемент текущей выбранной опции в выпадающем списке.
   *
   * @returns {HTMLElement | null}
   */
  get selectedOptionElement() {
    return (
      this.optionsContainer.querySelector(
        `.select__option[data-value="${CSS.escape(this.select.value)}"]`
      ) || null
    )
  }

  /**
   * Переключает открытое состояние выпадающего списка.
   *
   * @param {Event} e — событие клика по кнопке открытия
   */
  toggle(e) {
    e.preventDefault()
    e.stopPropagation()
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  /**
   * Открывает выпадающий список, позиционирует его и активирует стартовую опцию.
   *
   * @param {{ activeOption?: HTMLElement | null }} [options] — параметры открытия
   */
  open({ activeOption = null } = {}) {
    // Закрываем все остальные селекты
    this.constructor.closeOthers(this.element)

    this.isOpen = true
    this.element.classList.add('is-open')
    this.dropdown.classList.add('is-open')
    this.toggleTarget.setAttribute('aria-expanded', 'true')
    this.syncDropdownTheme()
    this.positionDropdown()
    this.setActiveOption(
      activeOption || this.selectedOptionElement || this.visibleOptions[0]
    )

    if (this.searchInput) setTimeout(() => this.searchInput.focus(), 100)
    document.addEventListener('scroll', this.boundRepositionDropdown, true)
    window.addEventListener('scroll', this.boundRepositionDropdown, true)
    window.addEventListener('resize', this.boundRepositionDropdown)
    document.addEventListener('click', this.boundHandleClickOutside)
  }

  /**
   * Закрывает выпадающий список, сбрасывает поиск, активную опцию и временные подписки.
   */
  close() {
    this.isOpen = false
    this.openDirection = null
    if (this.repositionFrame) {
      cancelAnimationFrame(this.repositionFrame)
      this.repositionFrame = null
    }
    this.element.classList.remove('is-open')
    this.dropdown.classList.remove('is-open')
    this.toggleTarget.setAttribute('aria-expanded', 'false')

    if (this.searchInput) {
      this.searchInput.value = ''
      this.filterOptions('')
    }

    this.setActiveOption(null)

    document.removeEventListener('click', this.boundHandleClickOutside)
    document.removeEventListener('scroll', this.boundRepositionDropdown, true)
    window.removeEventListener('scroll', this.boundRepositionDropdown, true)
    window.removeEventListener('resize', this.boundRepositionDropdown)
  }

  /**
   * Определяет направление открытия выпадающего списка с учётом доступного места.
   *
   * @returns {'top' | 'bottom'}
   */
  resolveOpenDirection() {
    const selectRect = this.toggleTarget.getBoundingClientRect()

    if (this.portalTarget === document.body) {
      const spaceBelow = window.innerHeight - selectRect.bottom
      return spaceBelow < this.dropdown.offsetHeight &&
        selectRect.top > spaceBelow
        ? 'top'
        : 'bottom'
    }

    const portalRect = this.portalTarget.getBoundingClientRect()
    const spaceBelow = portalRect.bottom - selectRect.bottom
    const spaceAbove = selectRect.top - portalRect.top

    return spaceBelow < this.dropdown.offsetHeight && spaceAbove > spaceBelow
      ? 'top'
      : 'bottom'
  }

  /**
   * Рассчитывает и применяет позицию выпадающего списка относительно кнопки открытия.
   *
   * @param {{ recalculateDirection?: boolean }} [options] — нужно ли заново выбирать направление открытия
   */
  positionDropdown({ recalculateDirection = true } = {}) {
    const selectRect = this.toggleTarget.getBoundingClientRect()
    const gap = 12 // Отступ между select и выпадающим списком
    const rootFontSize = getRootFontSize()
    const pxToRem = (px) => px / rootFontSize

    if (recalculateDirection || !this.openDirection) {
      this.openDirection = this.resolveOpenDirection()
    }

    const showAbove = this.openDirection === 'top'

    if (this.portalTarget === document.body) {
      const top = showAbove
        ? selectRect.top + window.scrollY - this.dropdown.offsetHeight - gap
        : selectRect.bottom + window.scrollY + gap

      this.dropdown.style.top = `${pxToRem(top)}rem`
      this.dropdown.style.left = `${pxToRem(selectRect.left + window.scrollX)}rem`
      this.dropdown.style.minWidth = `${pxToRem(selectRect.width)}rem`
      this.dropdown.classList.toggle('select__dropdown--top', showAbove)
      return
    }

    const portalRect = this.portalTarget.getBoundingClientRect()
    const top = showAbove
      ? selectRect.top - portalRect.top - this.dropdown.offsetHeight - gap
      : selectRect.bottom - portalRect.top + gap

    this.dropdown.style.position = 'absolute'
    this.dropdown.style.top = `${pxToRem(top)}rem`
    this.dropdown.style.left = `${pxToRem(selectRect.left - portalRect.left)}rem`
    this.dropdown.style.minWidth = `${pxToRem(selectRect.width)}rem`
    this.dropdown.classList.toggle('select__dropdown--top', showAbove)
  }

  /**
   * Выбирает опцию, закрывает выпадающий список и возвращает фокус на кнопку открытия.
   *
   * @param {string} value — значение выбранной опции
   */
  selectOption(value) {
    this.setValue(value)
    this.reset()
    this.close()
    this.toggleTarget.focus()
  }

  /**
   * Программно устанавливает значение нативного `<select>` и отправляет
   * событие `change`.
   *
   * @param {string | null} value — новое значение select
   */
  setValue(value) {
    this.select.value = value
    Array.from(this.select.options).forEach((opt) => {
      opt.selected = opt.value === value
    })
    this.select.dispatchEvent(new Event('change', { bubbles: true }))
    this.updateDisplay()
  }

  /**
   * Обновляет `aria-selected` у всех DOM-опций выпадающего списка.
   *
   * @param {string} value — текущее значение нативного `<select>`
   */
  updateSelectedOption(value) {
    this.optionsContainer
      .querySelectorAll('.select__option')
      .forEach((opt) =>
        opt.setAttribute('aria-selected', opt.dataset.value === value)
      )
  }

  /**
   * Делает опцию активной для навигации с клавиатуры и `aria-activedescendant`.
   *
   * @param {HTMLElement | null | undefined} option — DOM-элемент опции
   */
  setActiveOption(option) {
    if (this.activeOption) {
      this.activeOption.classList.remove('is-active')
    }

    this.activeOption = option

    if (!option) {
      this.toggleTarget.removeAttribute('aria-activedescendant')
      return
    }

    option.classList.add('is-active')
    this.toggleTarget.setAttribute('aria-activedescendant', option.id)
    option.scrollIntoView({ block: 'nearest' })
  }

  /**
   * Перемещает активную опцию вверх или вниз по видимому списку.
   *
   * @param {number} direction — направление движения: `1` вниз, `-1` вверх
   */
  moveActiveOption(direction) {
    const options = this.visibleOptions
    if (!options.length) {
      this.setActiveOption(null)
      return
    }

    const currentIndex = options.indexOf(this.activeOption)
    let nextIndex = (currentIndex + direction + options.length) % options.length

    if (currentIndex === -1) {
      nextIndex = direction > 0 ? 0 : options.length - 1
    }

    this.setActiveOption(options[nextIndex])
  }

  /**
   * Обрабатывает клавиатурное управление выпадающим списком.
   *
   * Поддерживает стрелки, Home/End, Enter/Space и Escape.
   *
   * @param {KeyboardEvent} event — событие `keydown`
   */
  handleKeydown(event) {
    const firstOption = this.visibleOptions[0]
    const lastOption = this.visibleOptions[this.visibleOptions.length - 1]

    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        event.preventDefault()
        if (this.isOpen) {
          this.moveActiveOption(1)
        } else {
          this.open({ activeOption: this.selectedOptionElement || firstOption })
        }
        break
      case 'ArrowUp':
      case 'Up':
        event.preventDefault()
        if (this.isOpen) {
          this.moveActiveOption(-1)
        } else {
          this.open({ activeOption: this.selectedOptionElement || lastOption })
        }
        break
      case 'Home':
        if (!this.isOpen) return
        event.preventDefault()
        this.setActiveOption(firstOption)
        break
      case 'End':
        if (!this.isOpen) return
        event.preventDefault()
        this.setActiveOption(lastOption)
        break
      case 'Enter':
      case ' ':
        if (event.key === ' ' && event.target === this.searchInput) return
        if (!this.isOpen) return
        event.preventDefault()
        if (this.activeOption)
          this.selectOption(this.activeOption.dataset.value)
        break
      case 'Escape':
      case 'Esc':
        if (!this.isOpen) return
        event.preventDefault()
        this.close()
        this.toggleTarget.focus()
        break
      default:
        break
    }
  }

  /**
   * Обработчик ввода в поисковое поле.
   *
   * @param {InputEvent} e — событие `input`
   */
  handleSearchInput(e) {
    this.filterOptions(e.target.value)
  }

  /**
   * Синхронизирует видимый текст, placeholder, изображение и выбранную опцию
   * с текущим значением нативного `<select>`.
   */
  updateDisplay() {
    const selected = this.select.options[this.select.selectedIndex]
    const hasValue = selected && !selected.hasAttribute('data-placeholder')

    this.element.classList.toggle('is-entered', !!hasValue)

    if (this.hasPlaceholderTarget)
      this.placeholderTarget.style.display = hasValue ? 'none' : ''

    if (this.hasTextTarget) {
      this.textTarget.textContent = hasValue ? selected.textContent : ''
      this.textTarget.style.display = hasValue ? '' : 'none'
    }

    this.updateToggleImage(selected)

    if (this.optionsContainer) this.updateSelectedOption(this.select.value)
  }

  /**
   * Обновляет изображение в кнопке открытия по `data-image` выбранной опции.
   *
   * @param {HTMLOptionElement | undefined} selectedOption — выбранная опция нативного `<select>`
   */
  updateToggleImage(selectedOption) {
    if (!this.hasToggleTarget) return

    const image = selectedOption?.dataset.image
    const toggleImage = this.toggleTarget.querySelector('img')

    if (image && toggleImage) {
      toggleImage.src = image
      return
    }

    if (image && !toggleImage) {
      const img = document.createElement('img')
      img.src = image
      img.alt = ''
      img.width = 18
      img.height = 18
      this.toggleTarget.insertAdjacentElement('afterbegin', img)
    }

    if (!image && toggleImage) {
      toggleImage.remove()
    }

    this.element.classList.toggle('has-image', !!image)
  }

  /**
   * Фильтрует опции выпадающего списка по строке поиска и показывает сообщение,
   * если совпадений нет.
   *
   * @param {string} term — поисковая строка
   */
  filterOptions(term) {
    const search = term.toLowerCase()
    let visibleCount = 0

    this.optionsContainer.querySelectorAll('.select__option').forEach((opt) => {
      const visible = opt.textContent.toLowerCase().includes(search)
      opt.classList.toggle('is-hidden', !visible)
      if (visible) visibleCount++
    })

    // Показываем/скрываем "Nothing found"
    let notFound = this.optionsContainer.querySelector('.select__nothing-found')
    if (visibleCount === 0 && !notFound) {
      notFound = document.createElement('li')
      notFound.className = 'select__nothing-found'
      notFound.textContent = this.nothingFoundValue || 'Nothing found'
      this.optionsContainer.appendChild(notFound)
    } else if (visibleCount > 0 && notFound) {
      notFound.remove()
    }

    if (!this.visibleOptions.includes(this.activeOption)) {
      const selectedOption = this.selectedOptionElement
      const nextOption =
        selectedOption && !selectedOption.classList.contains('is-hidden')
          ? selectedOption
          : this.visibleOptions[0]

      this.setActiveOption(nextOption)
    }

    this.repositionDropdown({ recalculateDirection: false })
  }

  /**
   * Закрывает выпадающий список при клике вне select и его портального списка.
   *
   * @param {MouseEvent} event — событие клика по документу
   */
  handleClickOutside(event) {
    if (
      !this.element.contains(event.target) &&
      !this.dropdown.contains(event.target)
    ) {
      this.close()
    }
  }

  /**
   * Планирует перерасчёт позиции выпадающего списка через `requestAnimationFrame`.
   *
   * @param {{ recalculateDirection?: boolean }} [options] — нужно ли заново выбирать направление открытия
   */
  repositionDropdown({ recalculateDirection = true } = {}) {
    if (!this.isOpen) return
    if (this.repositionFrame) return

    this.repositionFrame = requestAnimationFrame(() => {
      this.repositionFrame = null
      if (!this.isOpen) return

      this.positionDropdown({ recalculateDirection })
    })
  }

  /**
   * Проверка обязательности для form-validation_controller.js: у обязательного
   * select без выбранного значения показывает ошибку, как input#validate.
   * Подключается как validate() только у обязательных (см. connect): иначе
   * form-validation проверял бы и остальные селекты и стирал бы их серверные
   * ошибки, которых раньше не касался.
   *
   * @returns {boolean} true, если значение выбрано
   */
  validateRequired() {
    if (this.select.value) return true

    this.showErrors([this.requiredMessageValue || 'This field is required'])
    return false
  }

  /**
   * Сбрасывает состояние ошибки и удаляет отрендеренную подсказку.
   */
  reset() {
    this.element.classList.remove('is-invalid')

    if (this.#errorHintElement) {
      this.#errorHintElement.remove()
      this.#errorHintElement = null
    }

    if (this.hasErrorTarget) {
      this.errorTarget.remove()
    }
  }

  /**
   * Показывает или скрывает ошибки в select-компоненте.
   *
   * @param {string[]} errors — список текстов ошибок
   */
  showErrors(errors) {
    const hasErrors = errors.length > 0

    this.element.classList.toggle('is-invalid', hasErrors)

    if (this.#errorHintElement) {
      this.#errorHintElement.remove()
      this.#errorHintElement = null
    }

    if (this.hasErrorsTarget) {
      this.errorsTarget.innerHTML = ''
    }

    if (this.hasErrorsTarget && hasErrors) {
      this.#errorHintElement = createErrorHintElement(
        errors[0],
        this.element.classList.contains('input--no-inverse')
      )
      this.errorsTarget.appendChild(this.#errorHintElement)
    }
  }
}
